import apiClient from './api';
import { API_ENDPOINTS } from '@/config/api.config';

export enum TimeGranularity {
  WEEKLY = 'weekly',
  MONTHLY = 'monthly',
  QUARTERLY = 'quarterly',
  YEARLY = 'yearly'
}

export enum ActivityType {
  USER_ONBOARDED = 'user_onboarded',
  COURSE_ASSIGNED = 'course_assigned',
  MILESTONE_ACHIEVED = 'milestone_achieved',
  COURSE_COMPLETED = 'course_completed',
  ALERT = 'alert',
  LICENSE_RENEWAL = 'license_renewal'
}

export enum ActivityStatus {
  SUCCESS = 'success',
  INFO = 'info',
  WARNING = 'warning',
  ERROR = 'error'
}

export interface StatisticValue {
  value: number;
  change: number;
  trend: 'up' | 'down' | 'stable';
  previousValue?: number;
  percentageChange?: number;
  activeThisMonth?: number;
  currentlyAssigned?: number;
  organizationAverage?: number;
}

/** A tenant's own live credit position — matches the SDK CreditSummaryDto. */
export interface CreditSummary {
  has_plan: boolean;
  plan_key?: string;
  plan_name?: string;
  credits?: number;
  rollover_credits?: number;
  effective_credits?: number;
  used_credits?: number;
  remaining_credits?: number;
  dollar_value?: number;
  start_date?: string;
  end_date?: string;
  is_expired?: boolean;
  auto_renew?: boolean;
}

export interface DashboardMetadata {
  granularity: string;
  period?: {
    start: string;
    end: string;
  };
}

export interface DashboardStatsResponse {
  totalEmployees: StatisticValue;
  departments: StatisticValue;
  activeCourses: StatisticValue;
  completionRate: StatisticValue;
  meta_data?: DashboardMetadata;
}

export interface ActivityMetadata {
  user_id: string;
  user_name: string;
  user_email: string;
  action_by: string;
  action_by_name: string;
  action_by_email: string;
  user_agent: string;
  ip_address: string;
}

export interface DeviceInfo {
  browser: string;
  browser_version: string;
  os: string;
  os_version: string;
  device: string;
  user_agent: string;
}

export interface ActivityLog {
  _id: string;
  type: string;
  action: string;
  details: string;
  metadata: ActivityMetadata;
  timestamp: string;
  status: string;
  icon: string;
  device_info: DeviceInfo;
}

export interface RecentActivitiesResponse {
  success: boolean;
  code: number;
  description: string;
  data: ActivityLog[];
  meta_data: {
    total: number;
    skip:string;
    limit: string; // API returns limit as string
  };
}

export interface ActivityTrend {
  date: string;
  count: number;
  types: {
    user_onboarded: number;
    course_assigned: number;
    milestone_achieved: number;
    course_completed: number;
    alert: number;
    license_renewal: number;
  };
}

export interface UpcomingTraining {
  _id: string;
  course_id: string;
  title: string;
  description?: string;
  scheduled_date: string;
  departments: string[];
  department_ids: string[];
  is_mandatory: boolean;
  enrolled_count: number;
  total_capacity: number;
  status: 'scheduled' | 'in_progress' | 'completed' | 'cancelled';
  instructor?: {
    name: string;
    email: string;
  };
  duration_hours?: number;
  location?: string;
  meeting_link?: string;
}

export interface ActivityMetricEvent {
  event_type: string;
  event_name: string;
  count: number;
  percentage: number;
  icon: string;
  color: string;
}

export interface ActivityMetricsStatistics {
  most_frequent: string;
  least_frequent: string;
  avg_events_per_day: number;
}

export interface ActivityMetricsResponse {
  success: boolean;
  code: number;
  description: string;
  data: {
    events: ActivityMetricEvent[];
    total_events: number;
    unique_event_types: number;
    period_start: string;
    period_end: string;
    statistics: ActivityMetricsStatistics;
  };
}

export interface TimeBreakdown {
  label: string;
  value: number;
  date?: string;
}

export interface StatusBreakdown {
  not_started: TimeBreakdown[];
  in_progress: TimeBreakdown[];
  completed: TimeBreakdown[];
  assigned: TimeBreakdown[];
  total: number;
}

export interface AssignmentStatusStatsResponse {
  courses: StatusBreakdown;
  certifications: StatusBreakdown;
  learningPaths: StatusBreakdown;
}

export interface CertificateEarnedStatsResponse {
  courses: TimeBreakdown[];
  certifications: TimeBreakdown[];
  learningPaths: TimeBreakdown[];
  totals: {
    courses: number;
    certifications: number;
    learningPaths: number;
  };
}

class DashboardService {
  async getDashboardStats(
    granularity: TimeGranularity = TimeGranularity.MONTHLY,
    comparePrevious = true,
    customDates?: { start_date: string; end_date: string }
  ): Promise<DashboardStatsResponse> {
    try {
      // Build body: if custom dates provided, don't send granularity
      const body: any = {
        compare_previous: comparePrevious
      };

      if (customDates?.start_date && customDates?.end_date) {
        // Custom date range - don't send granularity
        body.start_date = customDates.start_date;
        body.end_date = customDates.end_date;
      } else {
        // Standard granularity
        body.granularity = granularity;
      }

      const response = await apiClient.post(API_ENDPOINTS.DASHBOARD.STATS, body);

      // Check if response has expected structure
      if (response.data && response.data.success && response.data.data) {
        return response.data.data;
      }

      // Return default structure if API fails
      console.warn('Dashboard stats API returned unexpected structure:', response.data);
      return {
        totalEmployees: { value: 0, change: 0, trend: 'stable' },
        departments: { value: 0, change: 0, trend: 'stable' },
        activeCourses: { value: 0, change: 0, trend: 'stable' },
        completionRate: { value: 0, change: 0, trend: 'stable' }
      } as DashboardStatsResponse;
    } catch (error) {
      console.error('Failed to fetch dashboard statistics:', error);
      // Return default structure on error
      return {
        totalEmployees: { value: 0, change: 0, trend: 'stable' },
        departments: { value: 0, change: 0, trend: 'stable' },
        activeCourses: { value: 0, change: 0, trend: 'stable' },
        completionRate: { value: 0, change: 0, trend: 'stable' }
      } as DashboardStatsResponse;
    }
  }

  async getRecentActivities(params: {
    skip?:number;
    limit?: number;
    types?: ActivityType[];
    status?: ActivityStatus;
    user_id?: string;
    course_id?: string;
    granularity?: TimeGranularity;
    start_date?: string;
    end_date?: string;
  } = {}): Promise<RecentActivitiesResponse> {
    try {
      // Build body: if custom dates provided, don't send granularity
      const body: any = {
        skip: params.skip || 0,
        limit: params.limit || 10,
      };

      // Add granularity or custom dates
      if (params.start_date && params.end_date) {
        // Custom date range
        body.start_date = params.start_date;
        body.end_date = params.end_date;
      } else if (params.granularity) {
        // Standard granularity
        body.granularity = params.granularity;
      }

      // Add other optional params
      if (params.types) body.types = params.types.join(',');
      if (params.status) body.status = params.status;
      if (params.user_id) body.user_id = params.user_id;
      if (params.course_id) body.course_id = params.course_id;

      const response = await apiClient.post(API_ENDPOINTS.DASHBOARD.RECENT_ACTIVITIES, body);
      
      // API returns the response directly with success, code, description, data, and meta_data
      if (response.data && response.data.success) {
        return {
          success: response.data.success,
          code: response.data.code,
          description: response.data.description,
          data: response.data.data || [],
          meta_data: response.data.meta_data || {
            total: 0,
            skip:String(params.skip || 10),
            limit: String(params.limit || 10)
          }
        };
      }
      
      // Return default structure on failure
      return {
        success: false,
        code: 500,
        description: 'Failed to fetch activities',
        data: [],
        meta_data: {
          total: 0,
          skip:String(params.skip || 10),
          limit: String(params.limit || 10)
        }
      };
    } catch (error) {
      console.error('Failed to fetch recent activities:', error);
      return {
        success: false,
        code: 500,
        description: 'Failed to fetch activities',
        data: [],
        meta_data: {
          total: 0,
          skip:String(params.skip || 10),
          limit: String(params.limit || 10)
        }
      };
    }
  }

   async getGroups(params: any = {}) {
    try {
      // Build request body with all parameters
      const body: any = {
        skip: params.skip || 0,
        limit: params.limit || 10,
      };

      // Optional filters
      if (params.location) body.location = params.location;
      if (params.group_name) body.group_name = params.group_name;
      if (params.group_key) body.group_key = params.group_key;
      if (params.with_users !== undefined) body.with_users = params.with_users;
      if (params.sort_by) body.sort_by = params.sort_by;
      if (params.sort_order) body.sort_order = params.sort_order;

      // Add granularity or custom dates
      if (params.start_date && params.end_date) {
        body.start_date = params.start_date;
        body.end_date = params.end_date;
      } else if (params.granularity) {
        body.granularity = params.granularity;
      }

      // POST request with body (no query params)
      const response = await apiClient.post(API_ENDPOINTS.DASHBOARD.DEPARTMENT_STATS, body);
      return response.data;
    } catch (error) {
      console.error('Error fetching groups:', error);
      throw error;
    }
  }

async getRecentActivitiesfilter(params: {
  skip?:number;
  limit?: number;
  action?: string;
  status?: ActivityStatus;
  user_id?: string;
  course_id?: string;
  granularity?: TimeGranularity;
  start_date?: string;
  end_date?: string;
} = {}): Promise<RecentActivitiesResponse> {
  try {
    // Build body: if custom dates provided, don't send granularity
    const body: any = {
      skip: params.skip || 0,
      limit: params.limit || 10,
    };

    // Add granularity or custom dates
    if (params.start_date && params.end_date) {
      // Custom date range
      body.start_date = params.start_date;
      body.end_date = params.end_date;
    } else if (params.granularity) {
      // Standard granularity
      body.granularity = params.granularity;
    }

    // Add other optional params
    if (params.action) body.action = params.action;
    if (params.status) body.status = params.status;
    if (params.user_id) body.user_id = params.user_id;
    if (params.course_id) body.course_id = params.course_id;

    const response = await apiClient.post(API_ENDPOINTS.DASHBOARD.RECENT_ACTIVITIES_FILTER, body);

    if (response.data && response.data.success) {
      return {
        success: response.data.success,
        code: response.data.code,
        description: response.data.description,
        data: response.data.data || [],
        meta_data: response.data.meta_data || {
          total: 0,
          skip:String(params.skip || 10),
          limit: String(params.limit || 10),
        }
      };
    }

    return {
      success: false,
      code: 500,
      description: 'Failed to fetch activities',
      data: [],
      meta_data: {
        total: 0,
        skip:String(params.skip || 10),
        limit: String(params.limit || 10)

      }
    };
  } catch (error) {
    console.error('Failed to fetch recent activities:', error);
    return {
      success: false,
      code: 500,
      description: 'Failed to fetch activities',
      data: [],
      meta_data: {
        total: 0,
        skip:String(params.skip || 10),
        limit: String(params.limit || 10)
      }
    };
  }
}


  async logActivity(activityData: {
    type: ActivityType;
    action: string;
    details: string;
    metadata?: ActivityMetadata;
    status?: ActivityStatus;
    icon?: string;
  }): Promise<ActivityLog> {
    const response = await apiClient.post(API_ENDPOINTS.DASHBOARD.LOG_ACTIVITY, activityData);
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to log activity');
    }
    
    return response.data.data;
  }

  async getActivityTrends(days = 7): Promise<ActivityTrend[]> {
    const response = await apiClient.get(API_ENDPOINTS.DASHBOARD.ACTIVITY_TRENDS, {
      params: { days }
    });
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to fetch activity trends');
    }
    
    return response.data.data;
  }

  async getUpcomingTrainings(params: {
    limit?: number;
    days_ahead?: number;
  } = {}): Promise<{ data: UpcomingTraining[]; total: number; hasMore: boolean }> {
    try {
      const response = await apiClient.get(API_ENDPOINTS.COURSE_ASSIGNMENTS.UPCOMING, {
        params: {
          limit: params.limit || 10,
          days_ahead: params.days_ahead || 30
        }
      });
      
      if (response.data && response.data.success) {
        return {
          data: response.data.data || [],
          total: response.data.meta?.total || 0,
          hasMore: response.data.meta?.hasMore || false
        };
      }
      
      return { data: [], total: 0, hasMore: false };
    } catch (error) {
      console.error('Failed to fetch upcoming trainings:', error);
      return { data: [], total: 0, hasMore: false };
    }
  }

  async getActivityMetrics(params: {
    granularity?: TimeGranularity;
    days?: number;
    start_date?: string;
    end_date?: string;
    user_id?: string;
    group_id?: string;
    limit?: number;
  } = {}): Promise<ActivityMetricsResponse> {
    try {
      // Build body: if custom dates provided, don't send granularity or days
      const body: any = {};

      if (params.start_date && params.end_date) {
        // Custom date range - send custom dates
        body.start_date = params.start_date;
        body.end_date = params.end_date;
      } else if (params.granularity) {
        // Standard granularity
        body.granularity = params.granularity;
      } else if (params.days) {
        // Legacy days support
        body.days = params.days;
      }

      // Add other optional params
      if (params.user_id) body.user_id = params.user_id;
      if (params.group_id) body.group_id = params.group_id;
      if (params.limit) body.limit = params.limit;

      const response = await apiClient.post(API_ENDPOINTS.DASHBOARD.ACTIVITY_METRICS, body);
      
      if (response.data && response.data.success) {
        return response.data;
      }
      
      // Return default structure on failure
      return {
        success: false,
        code: 500,
        description: 'Failed to fetch activity metrics',
        data: {
          events: [],
          total_events: 0,
          unique_event_types: 0,
          period_start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
          period_end: new Date().toISOString(),
          statistics: {
            most_frequent: '',
            least_frequent: '',
            avg_events_per_day: 0
          }
        }
      };
    } catch (error) {
      console.error('Failed to fetch activity metrics:', error);
      return {
        success: false,
        code: 500,
        description: 'Failed to fetch activity metrics',
        data: {
          events: [],
          total_events: 0,
          unique_event_types: 0,
          period_start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
          period_end: new Date().toISOString(),
          statistics: {
            most_frequent: '',
            least_frequent: '',
            avg_events_per_day: 0
          }
        }
      };
    }
  }

  async getAssignmentStatusStats(params: {
    granularity?: TimeGranularity;
    start_date?: string;
    end_date?: string;
  } = {}): Promise<AssignmentStatusStatsResponse> {
    try {
      const body: any = {};

      if (params.start_date && params.end_date) {
        body.start_date = params.start_date;
        body.end_date = params.end_date;
      } else if (params.granularity) {
        body.granularity = params.granularity;
      }

      const response = await apiClient.post(API_ENDPOINTS.DASHBOARD.ASSIGNMENT_STATUS_STATS, body);

      if (response.data && response.data.success && response.data.data) {
        return response.data.data;
      }

      return this.getDefaultAssignmentStatusStats();
    } catch (error) {
      console.error('Failed to fetch assignment status stats:', error);
      return this.getDefaultAssignmentStatusStats();
    }
  }

  private getDefaultAssignmentStatusStats(): AssignmentStatusStatsResponse {
    const empty: StatusBreakdown = { not_started: [], in_progress: [], completed: [], assigned: [], total: 0 };
    return {
      courses: { ...empty },
      certifications: { ...empty },
      learningPaths: { ...empty }
    };
  }

  async getCertificateEarnedStats(params: {
    granularity?: TimeGranularity;
    start_date?: string;
    end_date?: string;
  } = {}): Promise<CertificateEarnedStatsResponse> {
    try {
      const body: any = {};

      if (params.start_date && params.end_date) {
        body.start_date = params.start_date;
        body.end_date = params.end_date;
      } else if (params.granularity) {
        body.granularity = params.granularity;
      }

      const response = await apiClient.post(API_ENDPOINTS.DASHBOARD.CERTIFICATE_EARNED_STATS, body);

      if (response.data && response.data.success && response.data.data) {
        return response.data.data;
      }

      return this.getDefaultCertificateEarnedStats();
    } catch (error) {
      console.error('Failed to fetch certificate earned stats:', error);
      return this.getDefaultCertificateEarnedStats();
    }
  }

  private getDefaultCertificateEarnedStats(): CertificateEarnedStatsResponse {
    return {
      courses: [],
      certifications: [],
      learningPaths: [],
      totals: { courses: 0, certifications: 0, learningPaths: 0 }
    };
  }

  // Legacy methods for backward compatibility
  async getMetrics(): Promise<any> {
    const response = await apiClient.get(API_ENDPOINTS.DASHBOARD.STATS);
    return response.data;
  }

  async getCourseStatus(): Promise<any> {
    const response = await apiClient.get(API_ENDPOINTS.DASHBOARD.COURSE_STATUS);
    return response.data;
  }

  async getUserActivity(period = 'daily'): Promise<any> {
    const response = await apiClient.get(API_ENDPOINTS.DASHBOARD.ACTIVITY, { params: { period } });
    return response.data;
  }

  async getCertificationProgress(): Promise<any> {
    const response = await apiClient.get(API_ENDPOINTS.DASHBOARD.CERTIFICATION_PROGRESS);
    return response.data;
  }

  async getTopCourses(limit = 5): Promise<any> {
    const response = await apiClient.get(API_ENDPOINTS.DASHBOARD.TOP_COURSES, { params: { limit } });
    return response.data;
  }

  async getCertificationLeaderboard(limit = 5): Promise<any> {
    const response = await apiClient.get(API_ENDPOINTS.DASHBOARD.CERTIFICATION_LEADERBOARD, { params: { limit } });
    return response.data;
  }

  async getNotifications(): Promise<any> {
    const response = await apiClient.get(API_ENDPOINTS.DASHBOARD.NOTIFICATIONS);
    return response.data;
  }

  /**
   * The caller tenant's own live credit summary for the dashboard card.
   * Returns { has_plan: false } for tenants with no plan (unlimited AI) — the card is hidden then.
   */
  async getCreditSummary(): Promise<CreditSummary> {
    const response = await apiClient.post(API_ENDPOINTS.CREDIT_PLAN.MY_SUMMARY, {});
    return response.data.data;
  }
}

export const dashboardService = new DashboardService();
